import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger
} from '@/components/ui/dialog';
import {
  Landmark, Plus, Upload, History, RefreshCw, Search, Eye, Edit, Trash2,
  CheckCircle, XCircle, AlertTriangle, ArrowUpCircle, ArrowDownCircle, FileSpreadsheet,
  Settings, FileText, FileDown, Copy, ChevronDown, ChevronRight, Save, Zap, Info,
  Building, Calendar, Clock, DollarSign, Filter, RotateCcw, Lightbulb,
  Receipt, Link, ExternalLink, TrendingUp, TrendingDown, PiggyBank, Package, CreditCard,
  X
} from 'lucide-react';
import { DebouncedInput, DebouncedTextarea } from '../shared/DebouncedInputs';

/**
 * Tab Conciliación Bancaria: cargar cartolas, clasificar movimientos, motor de reglas,
 * conciliación con facturas. Recibe todo el estado/handlers vía props.
 */
const ConciliacionTab = (props) => {
  const {
    // Estado conciliación
    conciliacionData, cuentasBancarias, movimientosBancarios, historialCargas,
    busquedaMovimientos, setBusquedaMovimientos,
    filtroCuenta, setFiltroCuenta,
    filtroTipoMovimiento, setFiltroTipoMovimiento,
    filtroClasificacion, setFiltroClasificacion,
    movimientosFiltrados,
    periodoReferencia, setPeriodoReferencia,

    // Categorías
    categorias, categoriasFlat, filtroTipoCategoria, setFiltroTipoCategoria,
    resumenClasificacion, initializeCategorias,

    // Reglas
    reglas, reglaForm, setReglaForm, resetReglaForm, filtroReglasActivas, setFiltroReglasActivas,
    resumenReglas,

    // Diálogos
    showNuevaCuenta, setShowNuevaCuenta, nuevaCuenta, setNuevaCuenta,
    showCargarCartola, setShowCargarCartola, cuentaSeleccionadaCarga, setCuentaSeleccionadaCarga,
    archivoCartola, setArchivoCartola, cargandoCartola,
    showHistorialCargas, setShowHistorialCargas,
    showDetalleMovimiento, setShowDetalleMovimiento, movimientoSeleccionado,
    showDetalleCarga, setShowDetalleCarga, cargaSeleccionada,
    showClasificarModal, setShowClasificarModal, movimientoAClasificar,
    clasificacionForm, setClasificacionForm,
    showReglasPanel, setShowReglasPanel,
    showCrearRegla, setShowCrearRegla,

    // Handlers
    handleCrearCuenta, handleCargarCartola, handleVerDetalleCarga, handleVerDetalleMovimiento,
    handleCambiarEstadoMovimiento, handleAbrirConciliarFactura,
    abrirModalClasificar, handleGuardarClasificacion, handleQuitarClasificacion,
    handleCrearRegla, handleEliminarRegla, handleToggleRegla, handleDuplicarRegla,
    handleCrearReglaDesdeMovimiento, handleAceptarSugerencia, handleRecalcularSugerencias,
    handleInicializarReglas, handleCrearDatosDemo,
    fetchConciliacionData, fetchHistorialCargas,
    generarSugerencias,

    // Utils
    formatMonto,
    loadingConciliacion, creandoRegla, clasificando,
  } = props;

  return (
<div className='space-y-6'>
  {/* Header con título y botones principales */}
  <div className='flex items-center justify-between'>
    <div>
      <h3 className='text-xl font-semibold text-slate-900 flex items-center gap-2'>
        <Landmark className='w-5 h-5 text-blue-600' />
        Conciliación Bancaria
      </h3>
      <p className='text-sm text-slate-500'>
        Gestión de cuentas, cartolas y movimientos bancarios
      </p>
    </div>
    <div className='flex items-center gap-2'>
      <Button 
        variant='outline' 
        size='sm'
        onClick={handleRecalcularSugerencias}
        title='Recalcular sugerencias con reglas'
        data-testid='btn-recalcular'
      >
        <Zap className='w-4 h-4 mr-1' />
        Recalcular
      </Button>
      <Button 
        variant='outline' 
        size='sm'
        onClick={() => setShowReglasPanel(true)}
        data-testid='btn-reglas'
      >
        <Settings className='w-4 h-4 mr-1' />
        Reglas
      </Button>
      <Button 
        variant='outline' 
        size='sm'
        onClick={() => { fetchHistorialCargas(); setShowHistorialCargas(true); }}
        data-testid='btn-historial-cargas'
      >
        <History className='w-4 h-4 mr-1' />
        Historial
      </Button>
      <Button 
        variant='outline' 
        size='sm'
        onClick={() => setShowCargarCartola(true)}
        data-testid='btn-cargar-cartola'
      >
        <Upload className='w-4 h-4 mr-1' />
        Cargar Cartola
      </Button>
      <Button 
        size='sm'
        onClick={() => setShowNuevaCuenta(true)}
        className='bg-primary hover:bg-primary/90'
        data-testid='btn-nueva-cuenta'
      >
        <Plus className='w-4 h-4 mr-1' />
        Nueva Cuenta
      </Button>
    </div>
  </div>

  {/* Resumen superior */}
  <div className='grid grid-cols-2 md:grid-cols-6 gap-3'>
    <Card className='p-3 border-slate-200'>
      <div className='flex items-center gap-2 mb-1'>
        <Landmark className='w-4 h-4 text-blue-500' />
        <span className='text-xs text-slate-500'>Cuentas</span>
      </div>
      <p className='text-xl font-bold text-slate-900'>{conciliacionData.cuentas_activas}</p>
    </Card>
    <Card className='p-3 border-slate-200'>
      <div className='flex items-center gap-2 mb-1'>
        <FileSpreadsheet className='w-4 h-4 text-slate-500' />
        <span className='text-xs text-slate-500'>Movimientos</span>
      </div>
      <p className='text-xl font-bold text-slate-900'>{conciliacionData.movimientos_mes}</p>
    </Card>
    <Card className='p-3 border-slate-200 bg-green-50'>
      <div className='flex items-center gap-2 mb-1'>
        <ArrowUpCircle className='w-4 h-4 text-green-600' />
        <span className='text-xs text-slate-500'>Abonos</span>
      </div>
      <p className='text-lg font-bold text-green-700'>{formatMonto(conciliacionData.total_abonos)}</p>
    </Card>
    <Card className='p-3 border-slate-200 bg-red-50'>
      <div className='flex items-center gap-2 mb-1'>
        <ArrowDownCircle className='w-4 h-4 text-red-600' />
        <span className='text-xs text-slate-500'>Cargos</span>
      </div>
      <p className='text-lg font-bold text-red-700'>{formatMonto(conciliacionData.total_cargos)}</p>
    </Card>
    <Card className='p-3 border-slate-200 bg-blue-50'>
      <div className='flex items-center gap-2 mb-1'>
        <CheckCircle className='w-4 h-4 text-blue-600' />
        <span className='text-xs text-slate-500'>Clasificados</span>
      </div>
      <p className='text-xl font-bold text-blue-700'>{resumenClasificacion.clasificados}</p>
      <p className='text-xs text-blue-500'>{resumenClasificacion.porcentaje_clasificado}%</p>
    </Card>
    <Card className='p-3 border-slate-200 bg-amber-50'>
      <div className='flex items-center gap-2 mb-1'>
        <AlertTriangle className='w-4 h-4 text-amber-600' />
        <span className='text-xs text-slate-500'>Sin Clasificar</span>
      </div>
      <p className='text-xl font-bold text-amber-700'>{resumenClasificacion.sin_clasificar}</p>
      {resumenClasificacion.con_sugerencia > 0 && (
        <p className='text-xs text-blue-500'>{resumenClasificacion.con_sugerencia} con sugerencia</p>
      )}
    </Card>
  </div>

  {/* Filtros y búsqueda */}
  <Card className='p-4 border-slate-200'>
    <div className='flex flex-wrap items-center gap-3'>
      <div className='flex-1 min-w-[200px]'>
        <div className='relative'>
          <DebouncedInput
            type='text'
            placeholder='Buscar descripción, banco, contraparte...'
            value={busquedaMovimientos}
            onChange={(e) => setBusquedaMovimientos(e.target.value)}
            className='pl-8'
            data-testid='busqueda-movimientos'
          />
          <Search className='w-4 h-4 text-slate-400 absolute left-2.5 top-2.5' />
        </div>
      </div>
      <div>
        <Select value={filtroCuenta} onValueChange={setFiltroCuenta}>
          <SelectTrigger className='w-40' data-testid='filtro-cuenta'>
            <SelectValue placeholder='Cuenta' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='todas'>Todas las cuentas</SelectItem>
            {cuentasBancarias.map(cuenta => (
              <SelectItem key={cuenta.id} value={cuenta.id}>
                {cuenta.account_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Select value={filtroTipoMovimiento} onValueChange={setFiltroTipoMovimiento}>
          <SelectTrigger className='w-32' data-testid='filtro-tipo'>
            <SelectValue placeholder='Tipo' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='todos'>Todos</SelectItem>
            <SelectItem value='credit'>Abonos</SelectItem>
            <SelectItem value='debit'>Cargos</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Select value={filtroClasificacion} onValueChange={setFiltroClasificacion}>
          <SelectTrigger className='w-40' data-testid='filtro-clasificacion'>
            <SelectValue placeholder='Clasificación' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='todos'>Todos estados</SelectItem>
            <SelectItem value='unclassified'>Sin clasificar</SelectItem>
            <SelectItem value='suggested'>Con sugerencia</SelectItem>
            <SelectItem value='classified'>Clasificados</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Select value={filtroTipoCategoria} onValueChange={setFiltroTipoCategoria}>
          <SelectTrigger className='w-36' data-testid='filtro-tipo-categoria'>
            <SelectValue placeholder='Categoría' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='todos'>Todo tipo</SelectItem>
            <SelectItem value='income'>Ingresos</SelectItem>
            <SelectItem value='expense'>Egresos</SelectItem>
            <SelectItem value='transfer'>Transferencias</SelectItem>
            <SelectItem value='adjustment'>Ajustes</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className='flex gap-1'>
        <Button 
          variant='outline' 
          size='sm'
          onClick={generarSugerencias}
          title='Generar sugerencias automáticas'
        >
          <RefreshCw className='w-4 h-4' />
        </Button>
        <Button 
          variant='ghost' 
          size='sm'
          onClick={fetchConciliacionData}
          title='Actualizar'
        >
          <RefreshCw className='w-4 h-4' />
        </Button>
      </div>
    </div>
    {/* Botón para inicializar categorías si no hay */}
    {categorias.length === 0 && (
      <div className='mt-3 p-3 bg-blue-50 rounded-lg flex items-center justify-between'>
        <span className='text-sm text-blue-700'>No hay categorías configuradas. Inicie el catálogo base.</span>
        <Button size='sm' variant='outline' onClick={initializeCategorias}>
          Inicializar Categorías
        </Button>
      </div>
    )}
  </Card>

  {/* Tabla de movimientos */}
  {loadingConciliacion ? (
    <Card className='p-12 text-center border-slate-200'>
      <div className='animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-2'></div>
      <p className='text-slate-600'>Cargando movimientos...</p>
    </Card>
  ) : movimientosFiltrados.length === 0 ? (
    <Card className='p-12 text-center border-slate-200 border-dashed'>
      <Landmark className='w-12 h-12 text-slate-300 mx-auto mb-3' />
      <p className='text-slate-600 font-medium'>No hay movimientos bancarios</p>
      <p className='text-sm text-slate-400 mb-4'>Cargue una cartola o cree datos de demostración</p>
      <Button variant='outline' size='sm' onClick={handleCrearDatosDemo}>
        Crear Datos Demo
      </Button>
    </Card>
  ) : (
    <Card className='border-slate-200 overflow-hidden'>
      <div className='max-h-[500px] overflow-y-auto'>
        <table className='w-full text-sm'>
          <thead className='bg-slate-50 sticky top-0 z-10'>
            <tr>
              <th className='text-left p-2 font-semibold text-slate-600 border-b'>Fecha</th>
              <th className='text-left p-2 font-semibold text-slate-600 border-b'>Banco</th>
              <th className='text-left p-2 font-semibold text-slate-600 border-b'>Descripción</th>
              <th className='text-center p-2 font-semibold text-slate-600 border-b'>Tipo</th>
              <th className='text-right p-2 font-semibold text-slate-600 border-b'>Monto</th>
              <th className='text-left p-2 font-semibold text-slate-600 border-b'>Clasificación</th>
              <th className='text-left p-2 font-semibold text-slate-600 border-b'>Contraparte</th>
              <th className='text-center p-2 font-semibold text-slate-600 border-b'>Estado</th>
              <th className='text-center p-2 font-semibold text-slate-600 border-b'>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {movimientosFiltrados.map((mov) => {
              const classStatus = mov.classification_status || 'unclassified';
              const category = mov.final_category || mov.suggested_category;
              const isSuggested = !mov.final_category && mov.suggested_category;
              
              return (
                <tr 
                  key={mov.id}
                  className='border-b border-slate-100 hover:bg-slate-50 transition-colors'
                >
                  <td className='p-2'>
                    <p className='font-medium text-slate-900 text-xs'>
                      {new Date(mov.transaction_date + 'T00:00:00').toLocaleDateString('es-CL')}
                    </p>
                  </td>
                  <td className='p-2'>
                    <p className='font-medium text-slate-900 text-xs'>{mov.bank_name}</p>
                    <p className='text-xs text-slate-400'>{mov.account_name}</p>
                  </td>
                  <td className='p-2 max-w-[200px]'>
                    <p className='text-slate-700 truncate text-xs' title={mov.description_raw}>
                      {mov.description_raw}
                    </p>
                    {mov.external_reference && (
                      <p className='text-xs text-slate-400'>Ref: {mov.external_reference}</p>
                    )}
                  </td>
                  <td className='p-2 text-center'>
                    {mov.movement_type === 'credit' ? (
                      <Badge className='bg-green-100 text-green-700 border-green-200 text-xs px-1.5'>
                        <ArrowUpCircle className='w-3 h-3 mr-0.5' />
                        Abono
                      </Badge>
                    ) : (
                      <Badge className='bg-red-100 text-red-700 border-red-200 text-xs px-1.5'>
                        <ArrowDownCircle className='w-3 h-3 mr-0.5' />
                        Cargo
                      </Badge>
                    )}
                  </td>
                  <td className={`p-2 text-right font-mono text-xs font-semibold ${mov.movement_type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                    {mov.movement_type === 'credit' ? '+' : '-'}{formatMonto(mov.amount)}
                  </td>
                  {/* Columna de Clasificación */}
                  <td className='p-2'>
                    {category ? (
                      <div className='flex flex-col gap-0.5'>
                        <div className='flex items-center gap-1'>
                          {mov.classification?.suggestion_rule_id && (
                            <Zap className='w-3 h-3 text-amber-500' title='Sugerido por regla' />
                          )}
                          {mov.classification?.prediction_source === 'historical_pattern' && (
                            <Lightbulb className='w-3 h-3 text-purple-500' title='Patrón histórico' />
                          )}
                          <Badge 
                            variant='outline'
                            className={`text-xs px-1.5 ${isSuggested ? 'bg-blue-50 text-blue-700 border-blue-200 border-dashed' : 'bg-green-50 text-green-700 border-green-200'}`}
                          >
                            {category.name}
                            {isSuggested && mov.classification?.confidence_score && (
                              <span className='ml-1 opacity-70'>({Math.round(mov.classification.confidence_score)}%)</span>
                            )}
                          </Badge>
                        </div>
                        {isSuggested && mov.classification?.suggestion_reason && (
                          <p className='text-xs text-slate-400 truncate max-w-[150px]' title={mov.classification.suggestion_reason}>
                            {mov.classification.suggestion_reason}
                          </p>
                        )}
                      </div>
                    ) : (
                      <Badge variant='outline' className='bg-slate-50 text-slate-500 border-slate-200 text-xs'>
                        Sin clasificar
                      </Badge>
                    )}
                  </td>
                  {/* Columna de Contraparte */}
                  <td className='p-2'>
                    <p className='text-xs text-slate-600 truncate max-w-[100px]' title={mov.classification?.counterparty_name}>
                      {mov.classification?.counterparty_name || '-'}
                    </p>
                  </td>
                  {/* Estado de clasificación */}
                  <td className='p-2 text-center'>
                    <Badge 
                      variant='outline'
                      className={`text-xs ${
                        classStatus === 'unclassified' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                        classStatus === 'suggested' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                        'bg-green-50 text-green-700 border-green-200'
                      }`}
                    >
                      {classStatus === 'unclassified' ? 'Pendiente' :
                       classStatus === 'suggested' ? 'Sugerido' : 'Clasificado'}
                    </Badge>
                  </td>
                  {/* Acciones */}
                  <td className='p-2 text-center'>
                    <div className='flex justify-center gap-0.5'>
                      {/* Botón Conciliar (solo para abonos no conciliados) */}
                      {mov.movement_type === 'credit' && mov.review_status !== 'reconciled' && (
                        <Button 
                          size='sm' 
                          variant='ghost'
                          className='h-7 w-7 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50'
                          onClick={() => handleAbrirConciliarFactura(mov)}
                          title='Conciliar con factura'
                          data-testid={`btn-conciliar-${mov.id}`}
                        >
                          <Link className='w-3.5 h-3.5' />
                        </Button>
                      )}
                      {/* Badge si ya está conciliado */}
                      {mov.review_status === 'reconciled' && (
                        <Badge className='bg-purple-100 text-purple-700 border-purple-200 text-xs px-1'>
                          <Link className='w-3 h-3 mr-0.5' />
                          Conciliado
                        </Badge>
                      )}
                      <Button 
                        size='sm' 
                        variant='ghost'
                        className='h-7 w-7 p-0'
                        onClick={() => handleVerDetalleMovimiento(mov.id)}
                        title='Ver detalle'
                      >
                        <Eye className='w-3.5 h-3.5' />
                      </Button>
                      <Button 
                        size='sm' 
                        variant='ghost'
                        className='h-7 w-7 p-0 text-primary hover:text-primary/80'
                        onClick={() => abrirModalClasificar(mov)}
                        title='Clasificar'
                      >
                        <Edit className='w-3.5 h-3.5' />
                      </Button>
                      {isSuggested && (
                        <Button 
                          size='sm' 
                          variant='ghost'
                          className='h-7 w-7 p-0 text-green-600 hover:text-green-700'
                          onClick={() => handleAceptarSugerencia(mov.id)}
                          title='Aceptar sugerencia'
                        >
                          <CheckCircle className='w-3.5 h-3.5' />
                        </Button>
                      )}
                      {classStatus === 'classified' && (
                        <Button 
                          size='sm' 
                          variant='ghost'
                          className='h-7 w-7 p-0 text-slate-400 hover:text-red-500'
                          onClick={() => handleQuitarClasificacion(mov.id)}
                          title='Quitar clasificación'
                        >
                          <X className='w-3.5 h-3.5' />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className='p-3 bg-slate-50 border-t text-sm text-slate-600'>
        Mostrando {movimientosFiltrados.length} de {movimientosBancarios.length} movimientos
      </div>
    </Card>
  )}

  {/* Modal: Nueva Cuenta Bancaria */}
  <Dialog open={showNuevaCuenta} onOpenChange={setShowNuevaCuenta}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle className='flex items-center gap-2'>
          <Landmark className='w-5 h-5 text-primary' />
          Nueva Cuenta Bancaria
        </DialogTitle>
      </DialogHeader>
      <div className='space-y-4 mt-4'>
        <div>
          <Label>Banco *</Label>
          <Select 
            value={nuevaCuenta.bank_name} 
            onValueChange={(val) => setNuevaCuenta({...nuevaCuenta, bank_name: val})}
          >
            <SelectTrigger data-testid='select-banco'>
              <SelectValue placeholder='Seleccione banco' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='Banco de Chile'>Banco de Chile</SelectItem>
              <SelectItem value='Banco Santander'>Banco Santander</SelectItem>
              <SelectItem value='BCI'>BCI</SelectItem>
              <SelectItem value='Banco Estado'>Banco Estado</SelectItem>
              <SelectItem value='Scotiabank'>Scotiabank</SelectItem>
              <SelectItem value='Itaú'>Itaú</SelectItem>
              <SelectItem value='BICE'>BICE</SelectItem>
              <SelectItem value='Security'>Security</SelectItem>
              <SelectItem value='Otro'>Otro</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Nombre Identificador *</Label>
          <DebouncedInput
            placeholder='Ej: Cuenta Corriente Principal'
            value={nuevaCuenta.account_name}
            onChange={(e) => setNuevaCuenta({...nuevaCuenta, account_name: e.target.value})}
            data-testid='input-nombre-cuenta'
          />
        </div>
        <div>
          <Label>Número de Cuenta *</Label>
          <DebouncedInput
            placeholder='Ej: 00-123-45678-90'
            value={nuevaCuenta.account_number}
            onChange={(e) => setNuevaCuenta({...nuevaCuenta, account_number: e.target.value})}
            data-testid='input-numero-cuenta'
          />
        </div>
        <div className='grid grid-cols-2 gap-4'>
          <div>
            <Label>Moneda</Label>
            <Select 
              value={nuevaCuenta.currency} 
              onValueChange={(val) => setNuevaCuenta({...nuevaCuenta, currency: val})}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='CLP'>CLP - Peso Chileno</SelectItem>
                <SelectItem value='USD'>USD - Dólar</SelectItem>
                <SelectItem value='EUR'>EUR - Euro</SelectItem>
                <SelectItem value='UF'>UF</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className='flex items-center gap-2 pt-6'>
            <Checkbox 
              id='cuenta-activa'
              checked={nuevaCuenta.is_active}
              onCheckedChange={(val) => setNuevaCuenta({...nuevaCuenta, is_active: val})}
            />
            <Label htmlFor='cuenta-activa'>Cuenta activa</Label>
          </div>
        </div>
        <Button onClick={handleCrearCuenta} className='w-full' data-testid='btn-guardar-cuenta'>
          Crear Cuenta
        </Button>
      </div>
    </DialogContent>
  </Dialog>

  {/* Modal: Cargar Cartola */}
  <Dialog open={showCargarCartola} onOpenChange={setShowCargarCartola}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle className='flex items-center gap-2'>
          <Upload className='w-5 h-5 text-primary' />
          Cargar Cartola Bancaria
        </DialogTitle>
        <DialogDescription>
          Suba un archivo CSV o Excel con los movimientos bancarios
        </DialogDescription>
      </DialogHeader>
      <div className='space-y-4 mt-4'>
        <div>
          <Label>Cuenta Bancaria *</Label>
          <Select 
            value={cuentaSeleccionadaCarga} 
            onValueChange={setCuentaSeleccionadaCarga}
          >
            <SelectTrigger data-testid='select-cuenta-carga'>
              <SelectValue placeholder='Seleccione cuenta' />
            </SelectTrigger>
            <SelectContent>
              {cuentasBancarias.map(cuenta => (
                <SelectItem key={cuenta.id} value={cuenta.id}>
                  {cuenta.bank_name} - {cuenta.account_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Período de Referencia (opcional)</Label>
          <Input
            type='month'
            value={periodoReferencia}
            onChange={(e) => setPeriodoReferencia(e.target.value)}
            data-testid='input-periodo'
          />
        </div>
        <div>
          <Label>Archivo (CSV o Excel) *</Label>
          <Input
            type='file'
            accept='.csv,.xlsx,.xls'
            onChange={(e) => setArchivoCartola(e.target.files[0])}
            data-testid='input-archivo-cartola'
          />
          <p className='text-xs text-slate-500 mt-1'>
            El sistema detectará automáticamente las columnas: fecha, descripción, monto/abono/cargo, saldo, referencia
          </p>
        </div>
        {cuentasBancarias.length === 0 && (
          <div className='p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700'>
            <AlertTriangle className='w-4 h-4 inline mr-1' />
            Primero debe crear una cuenta bancaria
          </div>
        )}
        <Button 
          onClick={handleCargarCartola} 
          className='w-full' 
          disabled={cargandoCartola || !cuentaSeleccionadaCarga || !archivoCartola}
          data-testid='btn-procesar-cartola'
        >
          {cargandoCartola ? (
            <>
              <div className='animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2'></div>
              Procesando...
            </>
          ) : (
            <>
              <Upload className='w-4 h-4 mr-2' />
              Cargar y Procesar
            </>
          )}
        </Button>
      </div>
    </DialogContent>
  </Dialog>

  {/* Modal: Historial de Cargas */}
  <Dialog open={showHistorialCargas} onOpenChange={setShowHistorialCargas}>
    <DialogContent className='max-w-3xl'>
      <DialogHeader>
        <DialogTitle className='flex items-center gap-2'>
          <History className='w-5 h-5 text-primary' />
          Historial de Cargas
        </DialogTitle>
      </DialogHeader>
      <div className='mt-4 max-h-[400px] overflow-y-auto'>
        {historialCargas.length === 0 ? (
          <p className='text-center text-slate-500 py-8'>No hay cargas registradas</p>
        ) : (
          <table className='w-full text-sm'>
            <thead className='bg-slate-50'>
              <tr>
                <th className='text-left p-2 font-semibold text-slate-600'>Fecha</th>
                <th className='text-left p-2 font-semibold text-slate-600'>Banco</th>
                <th className='text-left p-2 font-semibold text-slate-600'>Cuenta</th>
                <th className='text-left p-2 font-semibold text-slate-600'>Archivo</th>
                <th className='text-center p-2 font-semibold text-slate-600'>Movimientos</th>
                <th className='text-center p-2 font-semibold text-slate-600'>Estado</th>
                <th className='text-center p-2 font-semibold text-slate-600'>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {historialCargas.map((carga) => (
                <tr key={carga.id} className='border-b border-slate-100 hover:bg-slate-50'>
                  <td className='p-2'>
                    {new Date(carga.upload_date).toLocaleDateString('es-CL')}
                  </td>
                  <td className='p-2'>{carga.bank_name}</td>
                  <td className='p-2'>{carga.account_name || carga.account_number}</td>
                  <td className='p-2 max-w-[150px] truncate' title={carga.file_name}>
                    {carga.file_name}
                  </td>
                  <td className='p-2 text-center'>
                    <span className='text-green-600'>{carga.movements_imported}</span>
                    {carga.movements_duplicated > 0 && (
                      <span className='text-slate-400'> / {carga.movements_duplicated} dup</span>
                    )}
                  </td>
                  <td className='p-2 text-center'>
                    <Badge 
                      variant='outline'
                      className={
                        carga.processing_status === 'processed' ? 'bg-green-50 text-green-700' :
                        carga.processing_status === 'error' ? 'bg-red-50 text-red-700' :
                        'bg-amber-50 text-amber-700'
                      }
                    >
                      {carga.processing_status === 'processed' ? 'Procesado' :
                       carga.processing_status === 'error' ? 'Error' : 'Subido'}
                    </Badge>
                  </td>
                  <td className='p-2 text-center'>
                    <Button 
                      size='sm' 
                      variant='ghost'
                      onClick={() => handleVerDetalleCarga(carga.id)}
                    >
                      <Eye className='w-4 h-4' />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </DialogContent>
  </Dialog>

  {/* Modal: Detalle de Movimiento */}
  <Dialog open={showDetalleMovimiento} onOpenChange={setShowDetalleMovimiento}>
    <DialogContent className='max-w-2xl'>
      <DialogHeader>
        <DialogTitle className='flex items-center gap-2'>
          <FileText className='w-5 h-5 text-primary' />
          Detalle del Movimiento
        </DialogTitle>
      </DialogHeader>
      {movimientoSeleccionado && (
        <div className='space-y-4 mt-4'>
          <div className='grid grid-cols-2 gap-4'>
            <div>
              <Label className='text-xs text-slate-500'>Banco</Label>
              <p className='font-medium'>{movimientoSeleccionado.account?.bank_name || '-'}</p>
            </div>
            <div>
              <Label className='text-xs text-slate-500'>Cuenta</Label>
              <p className='font-medium'>{movimientoSeleccionado.account?.account_name || '-'}</p>
            </div>
          </div>
          <div className='grid grid-cols-2 gap-4'>
            <div>
              <Label className='text-xs text-slate-500'>Fecha Movimiento</Label>
              <p className='font-medium'>
                {new Date(movimientoSeleccionado.transaction?.transaction_date + 'T00:00:00').toLocaleDateString('es-CL')}
              </p>
            </div>
            <div>
              <Label className='text-xs text-slate-500'>Tipo</Label>
              <Badge className={movimientoSeleccionado.transaction?.movement_type === 'credit' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                {movimientoSeleccionado.transaction?.movement_type === 'credit' ? 'Abono' : 'Cargo'}
              </Badge>
            </div>
          </div>
          <div>
            <Label className='text-xs text-slate-500'>Descripción Original</Label>
            <p className='font-medium text-sm bg-slate-50 p-2 rounded'>{movimientoSeleccionado.transaction?.description_raw}</p>
          </div>
          <div>
            <Label className='text-xs text-slate-500'>Descripción Normalizada</Label>
            <p className='text-sm bg-slate-50 p-2 rounded text-slate-600'>{movimientoSeleccionado.transaction?.description_normalized || '-'}</p>
          </div>
          <div className='grid grid-cols-2 gap-4'>
            <div>
              <Label className='text-xs text-slate-500'>Monto</Label>
              <p className={`text-xl font-bold ${movimientoSeleccionado.transaction?.movement_type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                {formatMonto(movimientoSeleccionado.transaction?.amount || 0)}
              </p>
            </div>
            <div>
              <Label className='text-xs text-slate-500'>Saldo</Label>
              <p className='text-xl font-bold text-slate-700'>
                {movimientoSeleccionado.transaction?.balance ? formatMonto(movimientoSeleccionado.transaction.balance) : '-'}
              </p>
            </div>
          </div>
          <div className='grid grid-cols-2 gap-4'>
            <div>
              <Label className='text-xs text-slate-500'>Referencia Externa</Label>
              <p className='font-mono text-sm'>{movimientoSeleccionado.transaction?.external_reference || '-'}</p>
            </div>
            <div>
              <Label className='text-xs text-slate-500'>Estado Revisión</Label>
              <Select 
                value={movimientoSeleccionado.transaction?.review_status || 'pending'}
                onValueChange={(val) => handleCambiarEstadoMovimiento(movimientoSeleccionado.transaction?.id, val)}
              >
                <SelectTrigger className='mt-1'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='pending'>Pendiente</SelectItem>
                  <SelectItem value='reviewed'>Revisado</SelectItem>
                  <SelectItem value='reconciled'>Conciliado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Bloque de Clasificación */}
          <div className='pt-4 border-t'>
            <h4 className='font-semibold text-slate-700 mb-3 flex items-center gap-2'>
              <Edit className='w-4 h-4' />
              Clasificación
            </h4>
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <Label className='text-xs text-slate-500'>Estado Clasificación</Label>
                <Badge 
                  variant='outline'
                  className={`mt-1 ${
                    movimientoSeleccionado.transaction?.classification_status === 'classified' ? 'bg-green-50 text-green-700' :
                    movimientoSeleccionado.transaction?.classification_status === 'suggested' ? 'bg-blue-50 text-blue-700' :
                    'bg-amber-50 text-amber-700'
                  }`}
                >
                  {movimientoSeleccionado.transaction?.classification_status === 'classified' ? 'Clasificado' :
                   movimientoSeleccionado.transaction?.classification_status === 'suggested' ? 'Sugerido' : 'Sin clasificar'}
                </Badge>
              </div>
              <div>
                <Label className='text-xs text-slate-500'>Fuente</Label>
                <p className='font-medium text-sm'>
                  {movimientoSeleccionado.classification?.classification_source === 'system' ? 'Sistema' :
                   movimientoSeleccionado.classification?.classification_source === 'manual' ? 'Manual' : '-'}
                </p>
              </div>
            </div>
            <div className='grid grid-cols-2 gap-4 mt-3'>
              <div>
                <Label className='text-xs text-slate-500'>Categoría Sugerida</Label>
                {movimientoSeleccionado.classification?.suggested_category ? (
                  <div className='flex items-center gap-2 mt-1'>
                    <Badge variant='outline' className='bg-blue-50 text-blue-700 border-dashed'>
                      {movimientoSeleccionado.classification.suggested_category.name}
                    </Badge>
                    {movimientoSeleccionado.classification?.confidence_score && (
                      <span className='text-xs text-blue-500'>
                        {movimientoSeleccionado.classification.confidence_score}%
                      </span>
                    )}
                  </div>
                ) : (
                  <p className='text-sm text-slate-400'>-</p>
                )}
              </div>
              <div>
                <Label className='text-xs text-slate-500'>Categoría Final</Label>
                {movimientoSeleccionado.classification?.final_category ? (
                  <Badge variant='outline' className='bg-green-50 text-green-700 mt-1'>
                    {movimientoSeleccionado.classification.final_category.name}
                  </Badge>
                ) : (
                  <p className='text-sm text-slate-400'>-</p>
                )}
              </div>
            </div>
            <div className='grid grid-cols-2 gap-4 mt-3'>
              <div>
                <Label className='text-xs text-slate-500'>Contraparte</Label>
                <p className='font-medium text-sm'>{movimientoSeleccionado.classification?.counterparty_name || '-'}</p>
              </div>
              <div>
                <Label className='text-xs text-slate-500'>Observaciones</Label>
                <p className='text-sm text-slate-600'>{movimientoSeleccionado.classification?.notes || '-'}</p>
              </div>
            </div>
            {movimientoSeleccionado.classification?.classified_by_name && (
              <div className='grid grid-cols-2 gap-4 mt-3'>
                <div>
                  <Label className='text-xs text-slate-500'>Clasificado por</Label>
                  <p className='text-sm'>{movimientoSeleccionado.classification.classified_by_name}</p>
                </div>
                <div>
                  <Label className='text-xs text-slate-500'>Fecha Clasificación</Label>
                  <p className='text-sm'>
                    {movimientoSeleccionado.classification.classified_at 
                      ? new Date(movimientoSeleccionado.classification.classified_at).toLocaleDateString('es-CL')
                      : '-'}
                  </p>
                </div>
              </div>
            )}
            {/* Botón para editar clasificación */}
            <Button 
              variant='outline' 
              size='sm' 
              className='mt-4'
              onClick={() => {
                // Cargar datos del movimiento completo para el modal de clasificación
                const movCompleto = movimientosBancarios.find(m => m.id === movimientoSeleccionado.transaction?.id);
                if (movCompleto) {
                  abrirModalClasificar(movCompleto);
                }
                setShowDetalleMovimiento(false);
              }}
            >
              <Edit className='w-4 h-4 mr-2' />
              Editar Clasificación
            </Button>
          </div>
          
          {movimientoSeleccionado.upload && (
            <div className='pt-2 border-t'>
              <Label className='text-xs text-slate-500'>Carga Origen</Label>
              <p className='text-sm'>
                {movimientoSeleccionado.upload.file_name} - {new Date(movimientoSeleccionado.upload.upload_date).toLocaleDateString('es-CL')}
              </p>
            </div>
          )}
        </div>
      )}
    </DialogContent>
  </Dialog>

  {/* Modal: Detalle de Carga */}
  <Dialog open={showDetalleCarga} onOpenChange={setShowDetalleCarga}>
    <DialogContent className='max-w-4xl'>
      <DialogHeader>
        <DialogTitle className='flex items-center gap-2'>
          <FileSpreadsheet className='w-5 h-5 text-primary' />
          Detalle de Carga: {cargaSeleccionada?.upload?.file_name}
        </DialogTitle>
      </DialogHeader>
      {cargaSeleccionada && (
        <div className='space-y-4 mt-4'>
          <div className='grid grid-cols-4 gap-4 text-sm'>
            <div>
              <Label className='text-xs text-slate-500'>Fecha Carga</Label>
              <p className='font-medium'>{new Date(cargaSeleccionada.upload?.upload_date).toLocaleDateString('es-CL')}</p>
            </div>
            <div>
              <Label className='text-xs text-slate-500'>Banco</Label>
              <p className='font-medium'>{cargaSeleccionada.account?.bank_name}</p>
            </div>
            <div>
              <Label className='text-xs text-slate-500'>Cuenta</Label>
              <p className='font-medium'>{cargaSeleccionada.account?.account_name}</p>
            </div>
            <div>
              <Label className='text-xs text-slate-500'>Movimientos</Label>
              <p className='font-medium'>{cargaSeleccionada.total_movements}</p>
            </div>
          </div>
          <div className='max-h-[350px] overflow-y-auto border rounded'>
            <table className='w-full text-sm'>
              <thead className='bg-slate-50 sticky top-0'>
                <tr>
                  <th className='text-left p-2 font-semibold text-slate-600'>Fecha</th>
                  <th className='text-left p-2 font-semibold text-slate-600'>Descripción</th>
                  <th className='text-center p-2 font-semibold text-slate-600'>Tipo</th>
                  <th className='text-right p-2 font-semibold text-slate-600'>Monto</th>
                  <th className='text-center p-2 font-semibold text-slate-600'>Estado</th>
                </tr>
              </thead>
              <tbody>
                {cargaSeleccionada.movements?.map((mov) => (
                  <tr key={mov.id} className='border-b border-slate-100'>
                    <td className='p-2'>{new Date(mov.transaction_date + 'T00:00:00').toLocaleDateString('es-CL')}</td>
                    <td className='p-2 max-w-[250px] truncate'>{mov.description_raw}</td>
                    <td className='p-2 text-center'>
                      <Badge className={mov.movement_type === 'credit' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                        {mov.movement_type === 'credit' ? 'Abono' : 'Cargo'}
                      </Badge>
                    </td>
                    <td className={`p-2 text-right font-mono ${mov.movement_type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                      {formatMonto(mov.amount)}
                    </td>
                    <td className='p-2 text-center'>
                      <Badge variant='outline' className={
                        mov.review_status === 'pending' ? 'bg-amber-50 text-amber-700' :
                        mov.review_status === 'reviewed' ? 'bg-blue-50 text-blue-700' :
                        'bg-green-50 text-green-700'
                      }>
                        {mov.review_status === 'pending' ? 'Pendiente' :
                         mov.review_status === 'reviewed' ? 'Revisado' : 'Conciliado'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </DialogContent>
  </Dialog>

  {/* Modal: Clasificar Movimiento */}
  <Dialog open={showClasificarModal} onOpenChange={setShowClasificarModal}>
    <DialogContent className='max-w-2xl'>
      <DialogHeader>
        <DialogTitle className='flex items-center gap-2'>
          <Edit className='w-5 h-5 text-primary' />
          Clasificar Movimiento
        </DialogTitle>
      </DialogHeader>
      {movimientoAClasificar && (
        <div className='space-y-4 mt-4'>
          {/* Datos del movimiento */}
          <div className='bg-slate-50 p-4 rounded-lg space-y-3'>
            <div className='grid grid-cols-3 gap-4'>
              <div>
                <Label className='text-xs text-slate-500'>Fecha</Label>
                <p className='font-medium'>
                  {new Date(movimientoAClasificar.transaction_date + 'T00:00:00').toLocaleDateString('es-CL')}
                </p>
              </div>
              <div>
                <Label className='text-xs text-slate-500'>Banco / Cuenta</Label>
                <p className='font-medium'>{movimientoAClasificar.bank_name}</p>
                <p className='text-xs text-slate-500'>{movimientoAClasificar.account_name}</p>
              </div>
              <div>
                <Label className='text-xs text-slate-500'>Tipo / Monto</Label>
                <p className={`font-bold text-lg ${movimientoAClasificar.movement_type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                  {movimientoAClasificar.movement_type === 'credit' ? '+' : '-'}{formatMonto(movimientoAClasificar.amount)}
                </p>
              </div>
            </div>
            <div>
              <Label className='text-xs text-slate-500'>Descripción Original</Label>
              <p className='font-medium text-sm'>{movimientoAClasificar.description_raw}</p>
            </div>
            <div>
              <Label className='text-xs text-slate-500'>Descripción Normalizada</Label>
              <p className='text-sm text-slate-600'>{movimientoAClasificar.description_normalized || '-'}</p>
            </div>
            {movimientoAClasificar.external_reference && (
              <div>
                <Label className='text-xs text-slate-500'>Referencia</Label>
                <p className='font-mono text-sm'>{movimientoAClasificar.external_reference}</p>
              </div>
            )}
          </div>

          {/* Sugerencia del sistema */}
          {movimientoAClasificar.suggested_category && !movimientoAClasificar.final_category && (
            <div className='bg-blue-50 border border-blue-200 p-3 rounded-lg'>
              <div className='flex items-center justify-between'>
                <div className='flex items-center gap-2'>
                  <RefreshCw className='w-4 h-4 text-blue-600' />
                  <span className='text-sm font-medium text-blue-700'>Sugerencia del sistema:</span>
                  <Badge className='bg-blue-100 text-blue-700'>
                    {movimientoAClasificar.suggested_category.name}
                  </Badge>
                  {movimientoAClasificar.classification?.confidence_score && (
                    <span className='text-xs text-blue-500'>
                      ({movimientoAClasificar.classification.confidence_score}% confianza)
                    </span>
                  )}
                </div>
                <Button 
                  size='sm' 
                  variant='outline'
                  className='text-blue-700 border-blue-300'
                  onClick={() => {
                    handleAceptarSugerencia(movimientoAClasificar.id);
                    setShowClasificarModal(false);
                  }}
                >
                  <CheckCircle className='w-4 h-4 mr-1' />
                  Aceptar
                </Button>
              </div>
            </div>
          )}

          {/* Formulario de clasificación */}
          <div className='space-y-4 pt-2'>
            <div>
              <Label>Categoría *</Label>
              <Select 
                value={clasificacionForm.category_id} 
                onValueChange={(val) => setClasificacionForm({...clasificacionForm, category_id: val})}
              >
                <SelectTrigger data-testid='select-categoria-clasificacion'>
                  <SelectValue placeholder='Seleccione categoría' />
                </SelectTrigger>
                <SelectContent className='max-h-[300px]'>
                  {/* INGRESOS */}
                  <div className='px-2 py-1 text-xs font-bold text-green-700 bg-green-50'>INGRESOS</div>
                  {categoriasFlat.filter(c => c.type === 'income' && !c.parent_category_id).map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                  {categoriasFlat.filter(c => c.type === 'income' && c.parent_category_id).map(cat => (
                    <SelectItem key={cat.id} value={cat.id} className='pl-6'>
                      ↳ {cat.name}
                    </SelectItem>
                  ))}
                  
                  {/* EGRESOS */}
                  <div className='px-2 py-1 text-xs font-bold text-red-700 bg-red-50 mt-1'>EGRESOS</div>
                  {categoriasFlat.filter(c => c.type === 'expense' && !c.parent_category_id).map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                  {categoriasFlat.filter(c => c.type === 'expense' && c.parent_category_id).map(cat => (
                    <SelectItem key={cat.id} value={cat.id} className='pl-6'>
                      ↳ {cat.name}
                    </SelectItem>
                  ))}
                  
                  {/* TRANSFERENCIAS */}
                  <div className='px-2 py-1 text-xs font-bold text-purple-700 bg-purple-50 mt-1'>TRANSFERENCIAS</div>
                  {categoriasFlat.filter(c => c.type === 'transfer').map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                  
                  {/* AJUSTES */}
                  <div className='px-2 py-1 text-xs font-bold text-slate-700 bg-slate-100 mt-1'>AJUSTES</div>
                  {categoriasFlat.filter(c => c.type === 'adjustment').map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Contraparte / Tercero</Label>
              <DebouncedInput
                placeholder='Nombre del cliente, proveedor o tercero'
                value={clasificacionForm.counterparty_name}
                onChange={(e) => setClasificacionForm({...clasificacionForm, counterparty_name: e.target.value})}
                data-testid='input-contraparte'
              />
            </div>

            <div>
              <Label>Observaciones</Label>
              <DebouncedInput
                placeholder='Notas adicionales sobre este movimiento'
                value={clasificacionForm.notes}
                onChange={(e) => setClasificacionForm({...clasificacionForm, notes: e.target.value})}
                data-testid='input-notas-clasificacion'
              />
            </div>

            <div className='flex gap-2 pt-2'>
              <Button 
                onClick={handleGuardarClasificacion} 
                className='flex-1'
                disabled={clasificando || !clasificacionForm.category_id}
                data-testid='btn-guardar-clasificacion'
              >
                {clasificando ? (
                  <>
                    <div className='animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2'></div>
                    Guardando...
                  </>
                ) : (
                  <>
                    <CheckCircle className='w-4 h-4 mr-2' />
                    Guardar Clasificación
                  </>
                )}
              </Button>
              <Button 
                variant='outline'
                onClick={() => handleCrearReglaDesdeMovimiento(movimientoAClasificar)}
                title='Crear regla para clasificar automáticamente movimientos similares'
              >
                <Zap className='w-4 h-4 mr-1' />
                Crear Regla
              </Button>
              <Button 
                variant='outline' 
                onClick={() => setShowClasificarModal(false)}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}
    </DialogContent>
  </Dialog>

  {/* Modal: Panel de Reglas de Clasificación */}
  <Dialog open={showReglasPanel} onOpenChange={setShowReglasPanel}>
    <DialogContent className='max-w-5xl max-h-[90vh] overflow-hidden flex flex-col'>
      <DialogHeader>
        <DialogTitle className='flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            <Settings className='w-5 h-5 text-primary' />
            Reglas de Clasificación
          </div>
          <div className='flex items-center gap-2'>
            <Badge variant='outline'>{resumenReglas.activas} activas</Badge>
            <Badge variant='outline'>{resumenReglas.auto_aplicar} auto-aplicar</Badge>
          </div>
        </DialogTitle>
      </DialogHeader>
      <div className='flex-1 overflow-y-auto'>
        {/* Acciones */}
        <div className='flex items-center justify-between mb-4'>
          <div className='flex items-center gap-2'>
            <Select value={filtroReglasActivas} onValueChange={setFiltroReglasActivas}>
              <SelectTrigger className='w-36'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='todas'>Todas</SelectItem>
                <SelectItem value='activas'>Activas</SelectItem>
                <SelectItem value='inactivas'>Inactivas</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className='flex gap-2'>
            {reglas.length === 0 && (
              <Button variant='outline' size='sm' onClick={handleInicializarReglas}>
                Cargar Reglas Demo
              </Button>
            )}
            <Button size='sm' onClick={() => { resetReglaForm(); setShowCrearRegla(true); }}>
              <Plus className='w-4 h-4 mr-1' />
              Nueva Regla
            </Button>
          </div>
        </div>

        {/* Tabla de reglas */}
        {reglas.length === 0 ? (
          <div className='text-center py-12 text-slate-500'>
            <Settings className='w-12 h-12 mx-auto mb-3 text-slate-300' />
            <p>No hay reglas configuradas</p>
            <p className='text-sm'>Cree reglas para clasificar movimientos automáticamente</p>
          </div>
        ) : (
          <table className='w-full text-sm'>
            <thead className='bg-slate-50'>
              <tr>
                <th className='text-left p-2 font-semibold text-slate-600'>Prioridad</th>
                <th className='text-left p-2 font-semibold text-slate-600'>Patrón</th>
                <th className='text-left p-2 font-semibold text-slate-600'>Tipo</th>
                <th className='text-left p-2 font-semibold text-slate-600'>Categoría</th>
                <th className='text-left p-2 font-semibold text-slate-600'>Contraparte</th>
                <th className='text-center p-2 font-semibold text-slate-600'>Auto</th>
                <th className='text-center p-2 font-semibold text-slate-600'>Uso</th>
                <th className='text-center p-2 font-semibold text-slate-600'>Éxito</th>
                <th className='text-center p-2 font-semibold text-slate-600'>Estado</th>
                <th className='text-center p-2 font-semibold text-slate-600'>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {reglas
                .filter(r => filtroReglasActivas === 'todas' || (filtroReglasActivas === 'activas' ? r.is_active : !r.is_active))
                .map((regla) => (
                <tr key={regla.id} className={`border-b border-slate-100 ${!regla.is_active ? 'opacity-50' : ''}`}>
                  <td className='p-2'>
                    <Badge variant='outline' className='bg-slate-100'>{regla.priority}</Badge>
                  </td>
                  <td className='p-2'>
                    <code className='text-xs bg-slate-100 px-1 rounded'>{regla.text_pattern}</code>
                    <span className='text-xs text-slate-400 ml-1'>({regla.match_type})</span>
                  </td>
                  <td className='p-2'>
                    {regla.movement_type === 'credit' ? (
                      <Badge className='bg-green-100 text-green-700 text-xs'>Abono</Badge>
                    ) : regla.movement_type === 'debit' ? (
                      <Badge className='bg-red-100 text-red-700 text-xs'>Cargo</Badge>
                    ) : (
                      <span className='text-slate-400 text-xs'>Todos</span>
                    )}
                  </td>
                  <td className='p-2'>
                    <Badge variant='outline' className={`text-xs ${regla.category_type === 'income' ? 'bg-green-50' : regla.category_type === 'expense' ? 'bg-red-50' : ''}`}>
                      {regla.category_name}
                    </Badge>
                  </td>
                  <td className='p-2 text-xs text-slate-600'>
                    {regla.counterparty_name || '-'}
                  </td>
                  <td className='p-2 text-center'>
                    {regla.auto_apply ? (
                      <CheckCircle className='w-4 h-4 text-green-500 mx-auto' />
                    ) : (
                      <X className='w-4 h-4 text-slate-300 mx-auto' />
                    )}
                  </td>
                  <td className='p-2 text-center text-xs'>{regla.usage_count}</td>
                  <td className='p-2 text-center'>
                    {regla.usage_count > 0 ? (
                      <span className={`text-xs font-medium ${(regla.success_count / regla.usage_count) >= 0.8 ? 'text-green-600' : 'text-amber-600'}`}>
                        {Math.round((regla.success_count / regla.usage_count) * 100)}%
                      </span>
                    ) : (
                      <span className='text-xs text-slate-400'>-</span>
                    )}
                  </td>
                  <td className='p-2 text-center'>
                    <Badge variant='outline' className={regla.is_active ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-500'}>
                      {regla.is_active ? 'Activa' : 'Inactiva'}
                    </Badge>
                  </td>
                  <td className='p-2 text-center'>
                    <div className='flex justify-center gap-0.5'>
                      <Button size='sm' variant='ghost' className='h-7 w-7 p-0' onClick={() => handleToggleRegla(regla.id)} title={regla.is_active ? 'Desactivar' : 'Activar'}>
                        {regla.is_active ? <X className='w-3.5 h-3.5' /> : <CheckCircle className='w-3.5 h-3.5' />}
                      </Button>
                      <Button size='sm' variant='ghost' className='h-7 w-7 p-0' onClick={() => handleDuplicarRegla(regla.id)} title='Duplicar'>
                        <Copy className='w-3.5 h-3.5' />
                      </Button>
                      <Button size='sm' variant='ghost' className='h-7 w-7 p-0 text-red-500' onClick={() => handleEliminarRegla(regla.id)} title='Eliminar'>
                        <Trash2 className='w-3.5 h-3.5' />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </DialogContent>
  </Dialog>

  {/* Modal: Crear/Editar Regla */}
  <Dialog open={showCrearRegla} onOpenChange={setShowCrearRegla}>
    <DialogContent className='max-w-2xl'>
      <DialogHeader>
        <DialogTitle className='flex items-center gap-2'>
          <Zap className='w-5 h-5 text-amber-500' />
          Nueva Regla de Clasificación
        </DialogTitle>
        <DialogDescription>
          Las reglas permiten clasificar automáticamente movimientos similares
        </DialogDescription>
      </DialogHeader>
      <div className='space-y-4 mt-4'>
        <div className='grid grid-cols-2 gap-4'>
          <div>
            <Label>Patrón de Texto *</Label>
            <DebouncedInput
              placeholder='ej: previred, honorario, comision'
              value={reglaForm.text_pattern}
              onChange={(e) => setReglaForm({...reglaForm, text_pattern: e.target.value})}
            />
          </div>
          <div>
            <Label>Tipo de Coincidencia</Label>
            <Select value={reglaForm.match_type} onValueChange={(val) => setReglaForm({...reglaForm, match_type: val})}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='contains'>Contiene</SelectItem>
                <SelectItem value='equals'>Exacto</SelectItem>
                <SelectItem value='starts_with'>Empieza con</SelectItem>
                <SelectItem value='regex'>Regex</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className='grid grid-cols-2 gap-4'>
          <div>
            <Label>Tipo de Movimiento</Label>
            <Select value={reglaForm.movement_type || 'any'} onValueChange={(val) => setReglaForm({...reglaForm, movement_type: val === 'any' ? '' : val})}>
              <SelectTrigger>
                <SelectValue placeholder='Cualquiera' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='any'>Cualquiera</SelectItem>
                <SelectItem value='credit'>Abonos</SelectItem>
                <SelectItem value='debit'>Cargos</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Categoría *</Label>
            <Select value={reglaForm.category_id} onValueChange={(val) => setReglaForm({...reglaForm, category_id: val})}>
              <SelectTrigger>
                <SelectValue placeholder='Seleccione categoría' />
              </SelectTrigger>
              <SelectContent className='max-h-[200px]'>
                {categoriasFlat.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.parent_category_id ? '↳ ' : ''}{cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className='grid grid-cols-2 gap-4'>
          <div>
            <Label>Contraparte (opcional)</Label>
            <DebouncedInput
              placeholder='ej: Microsoft, Previred'
              value={reglaForm.counterparty_name}
              onChange={(e) => setReglaForm({...reglaForm, counterparty_name: e.target.value})}
            />
          </div>
          <div>
            <Label>Cuenta Bancaria (opcional)</Label>
            <Select value={reglaForm.bank_account_id || 'all'} onValueChange={(val) => setReglaForm({...reglaForm, bank_account_id: val === 'all' ? '' : val})}>
              <SelectTrigger>
                <SelectValue placeholder='Todas las cuentas' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='all'>Todas las cuentas</SelectItem>
                {cuentasBancarias.map(cuenta => (
                  <SelectItem key={cuenta.id} value={cuenta.id}>
                    {cuenta.bank_name} - {cuenta.account_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className='grid grid-cols-3 gap-4'>
          <div>
            <Label>Monto Mínimo</Label>
            <DebouncedInput
              type='number'
              placeholder='Opcional'
              value={reglaForm.amount_min}
              onChange={(e) => setReglaForm({...reglaForm, amount_min: e.target.value})}
            />
          </div>
          <div>
            <Label>Monto Máximo</Label>
            <DebouncedInput
              type='number'
              placeholder='Opcional'
              value={reglaForm.amount_max}
              onChange={(e) => setReglaForm({...reglaForm, amount_max: e.target.value})}
            />
          </div>
          <div>
            <Label>Prioridad (1-100)</Label>
            <DebouncedInput
              type='number'
              min='1'
              max='100'
              value={reglaForm.priority}
              onChange={(e) => setReglaForm({...reglaForm, priority: parseInt(e.target.value) || 50})}
            />
          </div>
        </div>

        <div className='flex items-center gap-6 pt-2'>
          <div className='flex items-center gap-2'>
            <Checkbox 
              id='auto-apply'
              checked={reglaForm.auto_apply}
              onCheckedChange={(val) => setReglaForm({...reglaForm, auto_apply: val})}
            />
            <Label htmlFor='auto-apply'>Auto-aplicar sugerencia</Label>
          </div>
          <div className='flex items-center gap-2'>
            <Checkbox 
              id='is-active'
              checked={reglaForm.is_active}
              onCheckedChange={(val) => setReglaForm({...reglaForm, is_active: val})}
            />
            <Label htmlFor='is-active'>Regla activa</Label>
          </div>
        </div>

        <div className='flex gap-2 pt-4'>
          <Button onClick={handleCrearRegla} className='flex-1' disabled={creandoRegla || !reglaForm.text_pattern || !reglaForm.category_id}>
            {creandoRegla ? (
              <>
                <div className='animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2'></div>
                Creando...
              </>
            ) : (
              <>
                <Zap className='w-4 h-4 mr-2' />
                Crear Regla
              </>
            )}
          </Button>
          <Button variant='outline' onClick={() => setShowCrearRegla(false)}>
            Cancelar
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
</div>

  );
};

export default ConciliacionTab;
